import { useMemo, useState } from 'react';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import type { Tono } from '../componentes/Etiqueta';
import { DialogoMotivo } from '../componentes/DialogoMotivo';
import { haceCuanto, laFechaHora } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { ErrorApi } from '../dominio/clienteApi';
import { atenderIncidente, cerrarIncidente, obtenerColaIncidentes } from '../dominio/incidentesApi';
import type {
  EstadoIncidenteReal,
  IncidenteReal,
  TipoIncidenteReal,
} from '../dominio/incidentesApi';

/**
 * Etiquetas en castellano de los ENUMS REALES del backend. Deliberadamente NO reusan
 * `componentes/Etiqueta.tsx`: su diccionario `TIPO_INCIDENTE`/`INCIDENTE` está indexado por los
 * tipos MOCK de `dominio/contratos.ts`, que no coinciden con estos (ver la cabecera de
 * `dominio/incidentesApi.ts`) — mismo criterio que ya adoptó `Verificacion.tsx` para su propio
 * hueco de tipos. Mismas clases CSS (`etiqueta etiqueta--<tono>`), para que se vea igual.
 */
const TIPO_TEXTO: Record<TipoIncidenteReal, string> = {
  SOS: 'SOS',
  ACCIDENTE: 'Accidente',
  DISPUTA_RUTA: 'Disputa de ruta',
  NO_SHOW: 'No se presentó',
  CONDUCTA: 'Conducta',
  OTRO: 'Otro',
};

/** Sin `RESUELTO`: el backend real sólo tiene estos tres (ver `dominio/incidentesApi.ts`). */
const ESTADO_TEXTO: Record<EstadoIncidenteReal, [string, Tono]> = {
  ABIERTO: ['Abierto', 'malo'],
  EN_ATENCION: ['En atención', 'atencion'],
  CERRADO: ['Cerrado', 'neutro'],
};

function EtiquetaEstado({ valor }: { valor: EstadoIncidenteReal }) {
  const [texto, tono] = ESTADO_TEXTO[valor];
  return <span className={`etiqueta etiqueta--${tono}`}>{texto}</span>;
}

/** El mensaje que ve el operador cuando una mutación falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

export function Incidentes() {
  // Sin filtro de `estado` en la llamada a propósito: el backend sin `?estado=` ya trae TODOS
  // (verificado por efecto), y el filtro "sólo abiertos" de esta pantalla es un cruce de DOS
  // estados (ABIERTO + EN_ATENCION) — pedirle al backend uno solo por vez no alcanzaría sin dos
  // llamadas. Se filtra client-side, igual que hacía la versión mock.
  const [cola, recargarCola] = useCargaReal(() => obtenerColaIncidentes(), []);
  const [soloAbiertos, setSoloAbiertos] = useState(true);
  const [cerrando, setCerrando] = useState<IncidenteReal | null>(null);
  const [accionEnCurso, setAccionEnCurso] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const ahora = new Date();

  const listos = cola.estado === 'listo' ? cola.datos : [];
  const filas = useMemo(
    () =>
      listos.filter((incidente) =>
        soloAbiertos ? incidente.estado === 'ABIERTO' || incidente.estado === 'EN_ATENCION' : true,
      ),
    [listos, soloAbiertos],
  );

  /** Ejecuta una mutación, recarga la cola, y muestra el error si falla — mismo patrón que `Verificacion.tsx`. */
  async function ejecutar(clave: string, accion: () => Promise<unknown>): Promise<void> {
    setAccionEnCurso(clave);
    setErrorAccion(null);
    try {
      await accion();
      recargarCola();
      setCerrando(null);
    } catch (error) {
      setErrorAccion(mensajeDeFalla(error));
    } finally {
      setAccionEnCurso(null);
    }
  }

  return (
    <Pagina
      rotulo="Turno"
      titulo="Incidentes y disputas"
      ayuda="SOS, disputas y reclamos. Un SOS no detiene el viaje: abre un incidente en paralelo y lo que pasa después lo decide una persona."
    >
      {errorAccion ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError titulo="La acción no se pudo completar" detalle={errorAccion} />
        </div>
      ) : null}

      <div className="bloque">
        <div className="barra-filtros">
          <label className="fila">
            <input
              type="checkbox"
              checked={soloAbiertos}
              onChange={(evento) => setSoloAbiertos(evento.target.checked)}
            />
            <span>Sólo los que siguen abiertos</span>
          </label>
          <span className="tenue" style={{ marginLeft: 'auto' }}>
            {filas.length} de {listos.length}
          </span>
        </div>

        {cola.estado === 'cargando' ? (
          <Cargando filas={4} etiqueta="Cargando incidentes" />
        ) : cola.estado === 'error' ? (
          <ConError detalle={cola.mensaje} alReintentar={recargarCola} />
        ) : filas.length === 0 ? (
          <Vacio
            titulo={soloAbiertos ? 'Nada abierto' : 'Ningún incidente registrado'}
            detalle={
              soloAbiertos
                ? 'No hay nada esperando atención. Quitá el filtro para ver los ya resueltos.'
                : 'Cuando alguien dispare un SOS o abra un reclamo, aparece acá.'
            }
          />
        ) : (
          <Tabla cabeceras={['Abierto', 'Tipo', 'Estado', 'Viaje', 'Atiende', 'Qué pasó', '']}>
            {filas.map((incidente) => {
              const ocupado =
                accionEnCurso === `atender:${incidente.id}` ||
                accionEnCurso === `cerrar:${incidente.id}`;
              return (
                <tr key={incidente.id} data-estado={ESTADO_TEXTO[incidente.estado][1]}>
                  <td>
                    {haceCuanto(incidente.abiertoAt, ahora)}
                    <div className="apagado">{laFechaHora(incidente.abiertoAt)}</div>
                  </td>
                  <td>{TIPO_TEXTO[incidente.tipo]}</td>
                  <td>
                    <EtiquetaEstado valor={incidente.estado} />
                  </td>
                  <td className="tenue" style={{ fontFamily: 'var(--mono, monospace)' }}>
                    {incidente.viajeId ?? '—'}
                  </td>
                  <td
                    className={incidente.operadorId ? 'tenue' : 'apagado'}
                    style={
                      incidente.operadorId ? { fontFamily: 'var(--mono, monospace)' } : undefined
                    }
                  >
                    {/*
                      ⚠️ Sin nombre a propósito: `cola()` no hace `include` de la relación
                      `operador` (ver la cabecera de `dominio/incidentesApi.ts`) — sólo trae el
                      id crudo. Mismo patrón que `conductorId` sin nombre en `Dispatch.tsx`.
                    */}
                    {incidente.operadorId ?? 'nadie todavía'}
                  </td>
                  <td style={{ fontFamily: 'var(--sans)', maxWidth: 340 }}>
                    {incidente.descripcion ?? <span className="apagado">sin descripción</span>}
                    {incidente.resolucion ? (
                      <p className="apagado" style={{ marginTop: 6 }}>
                        Resolución: {incidente.resolucion}
                      </p>
                    ) : null}
                  </td>
                  <td>
                    <div className="fila">
                      <button
                        type="button"
                        className="boton boton--chico"
                        disabled={incidente.estado !== 'ABIERTO' || ocupado}
                        onClick={() =>
                          ejecutar(`atender:${incidente.id}`, () => atenderIncidente(incidente.id))
                        }
                      >
                        Tomar
                      </button>
                      <button
                        type="button"
                        className="boton boton--chico"
                        disabled={incidente.estado === 'CERRADO' || ocupado}
                        onClick={() => setCerrando(incidente)}
                      >
                        Cerrar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </Tabla>
        )}
      </div>

      <Bloque titulo="Un SOS no cancela el viaje">
        <p className="pagina__ayuda" style={{ marginTop: 0 }}>
          El viaje sigue su curso mientras el incidente está abierto. Es a propósito: si el SOS lo
          cortara, el sistema tendría que decidir qué pasa después, y eso lo decide quien está
          atendiendo. Para cortar un viaje en curso hay que hacerlo explícitamente desde el viaje.
        </p>
      </Bloque>

      {cerrando ? (
        <DialogoMotivo
          titulo={`Cerrar ${TIPO_TEXTO[cerrando.tipo].toLowerCase()} ${cerrando.id}`}
          descripcion="Contá cómo se resolvió. Si el pasajero o el conductor vuelven a preguntar por esto, lo que se lee es este texto. Mínimo 10 caracteres — lo exige el backend."
          etiquetaConfirmar="Cerrar incidente"
          alConfirmar={(motivo) =>
            ejecutar(`cerrar:${cerrando.id}`, () => cerrarIncidente(cerrando.id, motivo))
          }
          alCancelar={() => setCerrando(null)}
        />
      ) : null}
    </Pagina>
  );
}
